import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  FormItem,
  Input,
  Notification,
  toast,
} from "components/ui";
import { APIS } from "constants/api.constant";
import { putApi } from "services/CommonService";
import { editUserDetails, fetchUserDetail } from "store/profile";

const DefaultPasswordChangeModal = () => {
  const dispatch = useDispatch();
  const { info, loading } = useSelector((state) => state.profile || {});
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const profileData = info?.data ?? info;
  const mustChangePassword = profileData?.isDefaultPwd === true;

  useEffect(() => {
    dispatch(fetchUserDetail());
  }, [dispatch]);

  const handleSubmit = async () => {
    const current = oldPassword.trim();
    const next = newPassword.trim();
    const confirm = confirmPassword.trim();

    if (!current || !next || !confirm) {
      toast.push(
        <Notification type="warning" closable>
          Please enter your current password and new password.
        </Notification>,
      );
      return;
    }

    if (next.length < 6) {
      toast.push(
        <Notification type="warning" closable>
          Password must be at least 6 characters.
        </Notification>,
      );
      return;
    }

    if (next !== confirm) {
      toast.push(
        <Notification type="warning" closable>
          Passwords do not match.
        </Notification>,
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await putApi(APIS.CHANGE_PASSWORD, {
        oldPassword: current,
        newPassword: next,
      });
      dispatch(editUserDetails({ isDefaultPwd: false }));
      await dispatch(fetchUserDetail()).unwrap();
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.push(
        <Notification type="success" closable>
          {res?.message || "Password updated successfully."}
        </Notification>,
      );
    } catch (error) {
      console.error("Change password error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !info) {
    return null;
  }

  return (
    <Dialog
      isOpen={mustChangePassword}
      closable={false}
      width={480}
      title="Change your password"
      footer={
        <div className="flex items-center justify-end w-full">
          <Button
            variant="solid"
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          >
            Update password
          </Button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 mb-4">
        You must set a new password before you can continue using the admin
        panel. Use the temporary password you received when your account was
        created.
      </p>
      <div className="flex flex-col gap-4">
        <FormItem label="Current password" asterisk>
          <Input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="Enter current / temporary password"
            autoComplete="current-password"
            disabled={submitting}
          />
        </FormItem>
        <FormItem label="New password" asterisk>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            disabled={submitting}
          />
        </FormItem>
        <FormItem label="Confirm password" asterisk>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </FormItem>
      </div>
    </Dialog>
  );
};

export default DefaultPasswordChangeModal;
